/**
 * The page's rows, in spec §4.6's order of operations. Pure.
 * 1. The RPC's rows → Google Search and Shopping are left out (§4.2) and counted, before anything else (R8).
 * 2. Ad sets without a currency are dropped and counted; D1's currencyChoiceOf picks the page currency (§4.3).
 * 3. The channel and objective filters → "ซ่อน N" is counted (R7) → Active, when on (§4.4, R2). Ahead of all of these,
 *    even of step 1, the channel filter alone decides `empty` (rules-04 A3, as D2's E) on every fact: no spend in the
 *    channels in view, Search and Shopping included = no rows. A channel whose only spend is Search or Shopping did spend.
 *    Ahead of that, even of the facts' read, the one ticked channel known not to be connected = no rows (rules-04 H3).
 * 4. Per row: segment, metrics and chip; then "รวม", Share and box 1's segments over every row (§4.5).
 * 5. The sort. 6. The first SHOW_FIRST unless "แสดงทั้งหมด" is on.
 * No filter reads anything.
 *
 * @module services/audience/audienceView
 */
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import { isChannelShown } from '@/services/channelFilter';
import { segmentsOf } from '@/services/channels/channelBreakdown';
import { soleChannelLabel } from '@/services/channels/channelFormat';
import { breakdownMetricsOf } from '@/services/channels/channelMath';
import { idPrefix, targetingFamilyOf } from '@/services/channels/channelNaming';
import { creativeDataDateOf } from '@/services/creatives/creativeView';
import { currencyChoiceOf } from '@/services/overview/overviewFilters';
import { ratioOf } from '@/services/overview/overviewMath';
import type { ReadState } from '@/services/overview/overviewTypes';
import { audienceStateOf, isActiveCampaign, isSearchOrShopping } from './audienceRules';
import {
  SHOW_FIRST, type AudienceFact, type AudienceFilters, type AudienceRow, type AudienceTableData, type AudienceView,
} from './audienceTypes';

/** R5: D4 has no engagement columns, so D2's three engagement sums are null. */
const NO_ENGAGEMENT = { reactions: null, comments: null, shares: null } as const;

/** Code-unit order: the same on every machine, whatever the locale (R6: D3's one-liner, private there). */
const byText = (a: string, b: string): number => (a < b ? -1 : Number(a > b));

/** Higher first; NULL after every number, 0 included. */
const byNumberDesc = (a: number | null, b: number | null): number => {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
};

/** Step 5: the spend, highest first; then the name in code-unit order; then the ad-set id. */
export const sortAudienceRows = (rows: readonly AudienceRow[]): AudienceRow[] =>
  [...rows].sort((a, b) => byNumberDesc(a.spend, b.spend) || byText(a.name, b.name) || byText(a.key, b.key));

/** One ad set's row; its Share waits for every row. R3: a blank name shows the id's first 8 characters. */
const rowOf = (f: AudienceFact, yesterday: string): AudienceRow => ({
  key: f.adSetId,
  name: f.adSetName.trim() === '' ? idPrefix(f.adSetId) : f.adSetName,
  family: targetingFamilyOf(f.adSetName),
  platform: f.platform,
  externalAdSetId: f.externalAdSetId,
  campaignName: f.campaignName,
  state: audienceStateOf(f, yesterday),
  ...breakdownMetricsOf([{ spend: f.spend, impressions: f.impressions, clicks: f.clicks, ...NO_ENGAGEMENT }]),
  share: null,
});

/** R7, "ซ่อน N": the distinct campaigns that are not Active among the given facts. */
export const hiddenCampaignsOf = (facts: readonly AudienceFact[], yesterday: string): number =>
  new Set(facts.filter((f) => !isActiveCampaign(f, yesterday)).map((f) => f.campaignId)).size;

/** Steps 4–6 over the facts the filters kept: "รวม", Share and the segments over every row, never the first 15. */
const tableOf = (kept: readonly AudienceFact[], inCurrency: number, showAll: boolean, yesterday: string): AudienceTableData => {
  const unshared = kept.map((f) => rowOf(f, yesterday));
  const metrics = breakdownMetricsOf(unshared);
  const rows = sortAudienceRows(unshared.map((r) => ({ ...r, share: ratioOf(r.spend, metrics.spend) })));
  return {
    rows,
    shown: showAll ? rows : rows.slice(0, SHOW_FIRST),
    total: { ...metrics, share: ratioOf(metrics.spend, metrics.spend) },
    segments: segmentsOf(rows),
    inCurrency,
  };
};

/** Step 3's channel and objective filters; "ทั้งหมด" keeps every objective, unspecified included. */
const passes = (f: AudienceFact, filters: AudienceFilters, channels: ReadonlySet<string>): boolean =>
  isChannelShown(channels, f.platform) && (filters.objective === 'all' || f.objective === filters.objective);

export interface AudienceViewInput {
  facts: ReadState<AudienceFact[]>;
  filters: AudienceFilters;
  /** Bangkok's yesterday on the day the page is opened (spec §4.4). */
  yesterday: string;
  /**
   * The one ticked ad channel is connected (soleChannelConnectedOf, rules-04 G3); null = unknown: several channels or
   * none ticked, all workspaces, or the statuses read loading or failed. Only the card reads it: false puts it first,
   * before the facts, as on D2 (rules-04 H3).
   */
  channelConnected: boolean | null;
}

const NOTHING_YET: AudienceView = {
  money: null, unknownCurrency: 0, leftOut: null, hidden: null, latestFactDate: null, empty: null, table: { status: 'loading' },
};

const latestOf = (facts: readonly AudienceFact[]): string | null =>
  facts.reduce<string | null>((max, f) => (max === null || f.lastDate > max ? f.lastDate : max), null);

export const buildAudienceView = ({ facts, filters, yesterday, channelConnected }: AudienceViewInput): AudienceView => {
  // rules-04 H3, D2's order: the one ticked ad channel known not to be connected is the card before the facts are
  // looked at, over a table with no rows, as under the no-spend card; only for the one channel the sentence names
  // (rules-04 G review L1).
  const notConnected = channelConnected === false && soleChannelLabel(filters.channels) !== null;
  if (notConnected && facts.status !== 'ready') {
    return {
      ...NOTHING_YET,
      empty: { kind: 'notConnected', channels: [...filters.channels] },
      table: { status: 'ready', data: tableOf([], 0, filters.showAll, yesterday) },
    };
  }
  if (facts.status === 'failed') return { ...NOTHING_YET, table: { status: 'failed' } };
  if (facts.status !== 'ready') return NOTHING_YET;
  const kept = facts.data.filter((f) => !isSearchOrShopping(f));
  const money = currencyChoiceOf(kept, filters.currency);
  const priced = kept.filter((f) => f.currency !== null && f.currency === money.currency);
  const channels = new Set(filters.channels);
  // On every fact, before Search and Shopping are left out and before the objective, currency and Active filters:
  // a Search or Shopping ad group that spent, or a fact of unknown currency that spent, is spend.
  const noSpend = !facts.data.some((f) => isChannelShown(channels, f.platform) && f.spend !== null && f.spend > 0);
  const filtered = priced.filter((f) => passes(f, filters, channels));
  const active = filters.activeOnly ? filtered.filter((f) => isActiveCampaign(f, yesterday)) : filtered;
  // The card says the one ticked channel is not connected whenever that is known, spend or none (rules-04 G3, H3).
  const card = notConnected || noSpend;
  return {
    // A card shows no figure, so none leaves an ad set of unknown currency out: no flag and no note (rules-04 H3, I2).
    money: card ? { ...money, unknownExcluded: false } : money,
    unknownCurrency: card ? 0 : kept.filter((f) => f.currency === null).length,
    leftOut: facts.data.length - kept.length,
    // A card hides every row, not Active: no "ซ่อน N", as while the facts load (rules-04 H review F6, I2).
    hidden: card ? null : hiddenCampaignsOf(filtered, yesterday),
    latestFactDate: latestOf(facts.data),
    empty: card ? { kind: notConnected ? 'notConnected' : 'noSpend', channels: [...filters.channels] } : null,
    table: { status: 'ready', data: tableOf(card ? [] : active, priced.length, filters.showAll, yesterday) },
  };
};

/** R1: D3's rule under D4's name (the newest channel data-through once read, else the newest fact day), so the two pages cannot drift. */
export const audienceDataDateOf: (freshness: ChannelFreshnessState, view: Pick<AudienceView, 'latestFactDate'>) => string | null =
  creativeDataDateOf;
